import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollToTop, getScrollPosition } from '../utils/scrollUtils';

// Hook to manage scroll restoration behavior
export const useScrollRestoration = (options = {}) => {
  const {
    scrollToTopOnRouteChange = true,
    saveScrollPosition = false,
    restoreScrollPosition = false,
    scrollBehavior = 'auto',
    delay = 0
  } = options;

  const location = useLocation();
  const savedPositions = useRef({});
  const previousLocation = useRef(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = previousLocation.current;

    // Save scroll position before route change
    if (saveScrollPosition && prevPath !== currentPath) {
      const position = getScrollPosition();
      savedPositions.current[prevPath] = position;
    }

    // Handle scroll restoration after route change
    const handleScrollRestoration = () => {
      if (restoreScrollPosition && savedPositions.current[currentPath]) {
        // Restore saved position
        const { x, y } = savedPositions.current[currentPath];
        window.scrollTo({
          top: y,
          left: x,
          behavior: scrollBehavior
        });
      } else if (scrollToTopOnRouteChange) {
        // Scroll to top
        scrollToTop(scrollBehavior);
      }
    };

    // Apply delay if specified
    if (delay > 0) {
      const timeoutId = setTimeout(handleScrollRestoration, delay);
      return () => clearTimeout(timeoutId);
    } else {
      handleScrollRestoration();
    }

    // Update previous location
    previousLocation.current = currentPath;
  }, [location.pathname, scrollToTopOnRouteChange, saveScrollPosition, restoreScrollPosition, scrollBehavior, delay]);

  return {
    scrollToTop: () => scrollToTop(scrollBehavior),
    getCurrentPosition: getScrollPosition,
    savedPositions: savedPositions.current
  };
};

// Hook specifically for store pages
export const useStoreScrollRestoration = () => {
  return useScrollRestoration({
    scrollToTopOnRouteChange: true,
    scrollBehavior: 'auto',
    delay: 100 // Small delay to ensure DOM is ready
  });
};

export default useScrollRestoration;
